import {
  ApiError,
  MessageDirection,
  MessageResponse,
  MessagesService,
  MessageType,
  OrganizationResponseForAdmins,
  OrgMessageRecipient,
} from "client/openapi";
import { useContext, useEffect, useState, useRef } from "react";
import { PageStatus } from "types";
import { ChevronDownIcon, PaperPlaneIcon } from "@radix-ui/react-icons";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";

import { Tag } from "components/Tag/TagChip";
import { TagColor, TagSize } from "components/Tag";
import { ButtonFill } from "components/Button";
import { ButtonColor } from "components/Button";
import { ButtonSize } from "components/Button";
import { Button } from "components/Button";
import Notifications from "util/notifications";
import { DialogContent } from "components/Dialog";
import { Dialog } from "components/Dialog";
import { DialogTrigger } from "components/Dialog";
import UnknownContactDialog from "components/LinkUnknownContact/UnknownContactDialog";
import "components/LinkUnknownContact/index.css";

type TaggedUser =
  | {
      id: number;
      first_name: string;
      last_name: string;
    }
  | null
  | undefined;

type GroupedMessages = {
  [key: string]: MessageResponse[];
};

export default function OrgCommunication({
  organization,
  startDate,
  endDate,
  filterMessagesForStudentIds,
  filterMessagesForTutorIds,
  filterMessagesForParentIds,
}: {
  organization: OrganizationResponseForAdmins;
  startDate: Date;
  endDate: Date;
  filterMessagesForStudentIds?: number[];
  filterMessagesForTutorIds?: number[];
  filterMessagesForParentIds?: number[];
}) {
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [error, setError] = useState<ApiError>();
  const [expandedMessageId, setExpandedMessageId] = useState<number | null>(
    null
  );
  const [replyContent, setReplyContent] = useState<string>("");
  const [sendingReply, setSendingMessage] = useState(false);
  const [latestInboundMessage, setLatestInboundMessage] =
    useState<MessageResponse | null>(null);
  const [subjectInput, setSubjectInput] = useState<string>("");

  const [hasMoreAbove, setHasMoreAbove] = useState(false);

  const { currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );
  const { user } = useAuth0();
  const [groupedMessages, setGroupedMessages] = useState<GroupedMessages>({});

  // Add ref for the messages container
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const [showUnknownDialog, setShowUnknownDialog] = useState(false);
  const [selectedUnknownContact, setSelectedUnknownContact] = useState<{
    email?: string | null;
    phone_number?: string | null;
  } | null>(null);

  const filterMessageByRecipients = (message: MessageResponse): boolean => {
    // Check if message is outbound (has recipients) or inbound (has matched users)
    const isOutbound = message.direction === MessageDirection.OUTBOUND;

    // If all filters are empty, show all messages
    if (
      !filterMessagesForStudentIds?.length &&
      !filterMessagesForTutorIds?.length &&
      !filterMessagesForParentIds?.length
    ) {
      return true;
    }

    // For outbound messages, check recipients
    if (isOutbound) {
      let hasMatchingStudent = false;
      let hasMatchingTutor = false;
      let hasMatchingParent = false;

      // Only check student filter if it's not empty
      if (filterMessagesForStudentIds?.length) {
        hasMatchingStudent = (message.student_recipients || []).some((s) =>
          filterMessagesForStudentIds.includes(s.id)
        );
      }

      // Only check tutor filter if it's not empty
      if (filterMessagesForTutorIds?.length) {
        hasMatchingTutor = (message.tutor_recipients || []).some((t) =>
          filterMessagesForTutorIds.includes(t.id)
        );
      }

      // Only check parent filter if it's not empty
      if (filterMessagesForParentIds?.length) {
        hasMatchingParent = (message.parent_recipients || []).some((p) =>
          filterMessagesForParentIds.includes(p.id)
        );
      }

      return hasMatchingStudent || hasMatchingTutor || hasMatchingParent;
    }

    // For inbound messages, check matched users
    let hasMatchingStudent = false;
    let hasMatchingTutor = false;
    let hasMatchingParent = false;

    // Only check student filter if it's not empty
    if (filterMessagesForStudentIds?.length) {
      hasMatchingStudent = (message.matched_students || []).some((s) =>
        filterMessagesForStudentIds.includes(s.id)
      );
    }

    // Only check tutor filter if it's not empty
    if (filterMessagesForTutorIds?.length) {
      hasMatchingTutor = (message.matched_tutors || []).some((t) =>
        filterMessagesForTutorIds.includes(t.id)
      );
    }

    // Only check parent filter if it's not empty
    if (filterMessagesForParentIds?.length) {
      hasMatchingParent = (message.matched_parents || []).some((p) =>
        filterMessagesForParentIds.includes(p.id)
      );
    }

    return hasMatchingStudent || hasMatchingTutor || hasMatchingParent;
  };

  // Scroll to bottom when messages are expanded
  useEffect(() => {
    if (expandedMessageId && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
      setHasMoreAbove(container.scrollHeight > container.clientHeight);
    }
  }, [expandedMessageId]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setHasMoreAbove(el.scrollTop > 0);
  };

  async function fetchMessages() {
    setStatus(PageStatus.LOADING);
    const start = moment(startDate).startOf("day").toISOString();
    const until = moment(endDate).endOf("day").toISOString();

    try {
      const response = await MessagesService.getOrganizationCommunication({
        orgId: currently_selected_organization as number,
        startDate: start,
        endDate: until,
      });

      setMessages(response);
      setStatus(PageStatus.SUCCESS);
    } catch (e: any) {
      console.error(`Error: ${e.message}`);
      setStatus(PageStatus.ERROR);
      setError(e);
    }
  }

  useEffect(() => {
    if (currently_selected_organization) {
      fetchMessages();
    }
  }, [currently_selected_organization, startDate, endDate]);

  const handleExpandMessage = async (
    messageId: number,
    conversationMessages: MessageResponse[]
  ) => {
    if (expandedMessageId === messageId) {
      setExpandedMessageId(null);
      setLatestInboundMessage(null);
      setSubjectInput("");
      return;
    }

    setExpandedMessageId(messageId);

    // Find and set the latest inbound message
    const latestInbound = conversationMessages
      .filter((msg) => msg.direction === MessageDirection.INBOUND)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

    setLatestInboundMessage(latestInbound || null);
    setSubjectInput("");

    // Find all unread inbound messages in the conversation
    const unreadInboundMessages = conversationMessages.filter(
      (msg) => msg.direction === MessageDirection.INBOUND && !msg.seen
    );

    if (unreadInboundMessages.length > 0) {
      try {
        // Mark all unread messages as read
        await Promise.all(
          unreadInboundMessages.map((msg) =>
            MessagesService.markMessageAsRead({ messageId: msg.id })
          )
        );

        // Update the messages state to reflect the read status
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            unreadInboundMessages.some((unread) => unread.id === msg.id)
              ? { ...msg, seen: true }
              : msg
          )
        );
      } catch (error) {
        console.error("Error marking messages as read:", error);
      }
    }
  };

  const handleSendMessage = async (conversationMessages: MessageResponse[]) => {
    if (!replyContent.trim() || !currently_selected_organization) return;

    const latestMessage = conversationMessages[conversationMessages.length - 1];
    const isOutbound = latestMessage.direction === MessageDirection.OUTBOUND;
    const conversationKey = latestMessage.conversation_key || "";

    setSendingMessage(true);
    try {
      const newMessage = await MessagesService.createMessageToRecipients({
        requestBody: {
          m_type: MessageType.DIRECT,
          subject:
            conversationKey.includes("email") && subjectInput.trim()
              ? subjectInput
              : "",
          content: replyContent,
          sender_id: user?.sub,
          org_id: currently_selected_organization,
          email: conversationKey.includes("email"),
          sms: conversationKey.includes("sms"),
          recipient_types: isOutbound
            ? [
                ...(latestMessage.student_recipients?.length
                  ? [OrgMessageRecipient.STUDENTS]
                  : []),
                ...(latestMessage.tutor_recipients?.length
                  ? [OrgMessageRecipient.TUTORS]
                  : []),
                ...(latestMessage.parent_recipients?.length
                  ? [OrgMessageRecipient.PARENTS]
                  : []),
              ]
            : [
                ...(latestMessage.matched_students?.length
                  ? [OrgMessageRecipient.STUDENTS]
                  : []),
                ...(latestMessage.matched_tutors?.length
                  ? [OrgMessageRecipient.TUTORS]
                  : []),
                ...(latestMessage.matched_parents?.length
                  ? [OrgMessageRecipient.PARENTS]
                  : []),
              ],
          student_recipients: isOutbound
            ? latestMessage.student_recipients?.map((s) => s.id)
            : latestMessage.matched_students?.map((s) => s.id),
          tutor_recipients: isOutbound
            ? latestMessage.tutor_recipients?.map((t) => t.id)
            : latestMessage.matched_tutors?.map((t) => t.id),
          parent_recipients: isOutbound
            ? latestMessage.parent_recipients?.map((p) => p.id)
            : latestMessage.matched_parents?.map((p) => p.id),
          direction: MessageDirection.OUTBOUND,
          ...(latestInboundMessage && {
            message_being_replied_to_id: latestInboundMessage.id,
          }),
        },
      });

      // Clear the reply input and refresh messages
      setReplyContent("");
      setSubjectInput("");

      const newMessageWithConversationKey = {
        ...newMessage,
        conversation_key: conversationKey,
      };

      const updatedMessageGroup = [
        ...groupedMessages[conversationKey],
        newMessageWithConversationKey,
      ];

      updatedMessageGroup.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      setGroupedMessages({
        ...groupedMessages,
        [conversationKey]: updatedMessageGroup,
      });

      setExpandedMessageId(newMessageWithConversationKey.id);
    } catch (e: any) {
      console.error(`Error sending message: ${e.message}`);
      setError(e);
    } finally {
      setSendingMessage(false);
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const handleUserClick = (userType: string, userId: number) => {
    window.open(`/manage/${userType}s/${userId}`, "_blank");
  };

  const renderTags = (
    parents: TaggedUser[] = [],
    students: TaggedUser[] = [],
    tutors: TaggedUser[] = [],
    condense: boolean = true,
    conversationKey: string = ""
  ) => {
    const allTags = [
      ...(
        parents?.filter((p): p is NonNullable<TaggedUser> => p != null) || []
      ).map((p) => ({ ...p, type: "parent" as const, color: TagColor.BLUE })),
      ...(
        students?.filter((s): s is NonNullable<TaggedUser> => s != null) || []
      ).map((s) => ({ ...s, type: "student" as const, color: TagColor.GREEN })),
      ...(
        tutors?.filter((t): t is NonNullable<TaggedUser> => t != null) || []
      ).map((t) => ({ ...t, type: "tutor" as const, color: TagColor.GOLD })),
    ];

    // If no tags are present, show Unknown tag
    if (allTags.length === 0) {
      const latestUnknownMessage = groupedMessages[conversationKey].find((m) =>
        m.conversation_key?.includes("noLinkToPeopleInOrg")
      );

      return (
        <div className="user-tags my-2">
          <div className="inline-block">
            <Dialog
              open={showUnknownDialog}
              onOpenChange={setShowUnknownDialog}
            >
              <DialogTrigger>
                <div
                  onClick={() => {
                    if (latestUnknownMessage) {
                      setSelectedUnknownContact({
                        email: latestUnknownMessage.sender_email || null,
                        phone_number: latestUnknownMessage.sender_phone || null,
                      });
                      setShowUnknownDialog(true);
                    }
                  }}
                >
                  <Tag
                    size={TagSize.SMALL}
                    item={`Unknown (${
                      latestUnknownMessage?.sender_email ||
                      latestUnknownMessage?.sender_phone
                    })`}
                    color={TagColor.GRAY}
                  />
                </div>
              </DialogTrigger>
              <DialogContent className="link-unknown-contact-dialog-content">
                <UnknownContactDialog
                  inboundContactInfo={selectedUnknownContact as any}
                  onClose={() => {
                    setShowUnknownDialog(false);
                    fetchMessages();
                  }}
                  organization={organization}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      );
    }

    if (condense && allTags.length > 2) {
      const visibleTags = allTags.slice(0, 2);
      const remainingCount = allTags.length - 2;

      return (
        <div className="user-tags my-2">
          {visibleTags.map((user) => (
            <div
              key={`${user.type}-${user.id}`}
              onClick={() => handleUserClick(user.type, user.id)}
              className="inline-block cursor-pointer"
            >
              <Tag
                size={TagSize.SMALL}
                item={`${user.first_name} ${user.last_name}`}
                color={user.color}
              />
            </div>
          ))}
          <div className="inline-block">
            <Tag
              size={TagSize.SMALL}
              item={`+${remainingCount} more`}
              color={TagColor.GRAY}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="user-tags my-2">
        {allTags.map((user) => (
          <div
            key={`${user.type}-${user.id}`}
            onClick={() => handleUserClick(user.type, user.id)}
            className="inline-block cursor-pointer"
          >
            <Tag
              size={TagSize.SMALL}
              item={`${user.first_name} ${user.last_name}`}
              color={user.color}
            />
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    // Group messages by conversation key
    const newGroupedMessages = messages
      .filter(filterMessageByRecipients)
      .reduce((acc, message) => {
        // If conversation key contains "noLinkToPeopleInOrg", treat as standalone
        const key = message.conversation_key?.includes("noLinkToPeopleInOrg")
          ? `single-${message.id}` // Force standalone by using unique key
          : message.conversation_key || `single-${message.id}`;

        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(message);
        return acc;
      }, {} as GroupedMessages);

    // Sort messages within each group by creation date
    Object.keys(newGroupedMessages).forEach((key) => {
      newGroupedMessages[key].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });

    setGroupedMessages(newGroupedMessages);
  }, [
    messages,
    filterMessagesForStudentIds,
    filterMessagesForTutorIds,
    filterMessagesForParentIds,
  ]);

  useEffect(() => {
    if (error) {
      Notifications.error(error.message || "An unexpected error occurred.");
    }
  }, [error]);

  if (status === PageStatus.LOADING) {
    return <div>Loading...</div>;
  }

  if (!messages || messages.length === 0) {
    return <div>No messages</div>;
  }

  return (
    <table className="w-full bg-white border-collapse">
      <thead className="sticky top-0 bg-white z-1 shadow-sm">
        <tr className="border-b-2 border-gray-300">
          <th className="text-left p-2 text-sm font-bold w-[12.5%]">Date</th>
          <th className="text-left p-2 text-sm font-bold w-[15%]">Subject</th>
          <th className="text-left p-2 text-sm font-bold w-[37.5%]">Content</th>
          <th className="text-left p-2 text-sm font-bold w-[22.5%]">People</th>
          <th className="text-center p-2 text-sm font-bold w-[7.5%]">Status</th>
          <th className="text-center p-2 text-sm font-bold w-[5%]">Details</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(groupedMessages).map(
          ([conversationKey, conversationMessages]) => {
            const latestMessage =
              conversationMessages[conversationMessages.length - 1];
            const isExpanded = expandedMessageId === latestMessage.id;
            const hasUnreadInbound = conversationMessages.some(
              (msg) => msg.direction === MessageDirection.INBOUND && !msg.seen
            );

            return (
              <>
                <tr
                  key={latestMessage.id}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="p-2 text-sm">
                    {moment(latestMessage.created_at).format("MMM Do, h:mm a")}
                  </td>
                  <td className="p-2 text-sm">
                    {truncateText(latestMessage?.subject || "", 25)}
                  </td>
                  <td className="p-2 text-sm">
                    {truncateText(latestMessage.content, 75)}
                  </td>
                  <td className="text-sm font-bold">
                    {latestMessage.direction === MessageDirection.OUTBOUND
                      ? renderTags(
                          latestMessage.parent_recipients || [],
                          latestMessage.student_recipients || [],
                          latestMessage.tutor_recipients || [],
                          true,
                          conversationKey
                        )
                      : renderTags(
                          latestMessage.matched_parents || [],
                          latestMessage.matched_students || [],
                          latestMessage.matched_tutors || [],
                          true,
                          conversationKey
                        )}
                  </td>
                  <td className="p-2 text-center">
                    {hasUnreadInbound ? (
                      <div className="w-3 h-3 rounded-full bg-blue-500 mx-auto"></div>
                    ) : (
                      <div className="w-3 h-3 rounded-full bg-gray-200 mx-auto"></div>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() =>
                        handleExpandMessage(
                          latestMessage.id,
                          conversationMessages
                        )
                      }
                      className="p-1 hover:bg-gray-100 rounded-full transition-transform"
                      style={{
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      <ChevronDownIcon width="20" height="20" />
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td colSpan={6} className="p-4">
                      <div className="space-y-4">
                        <div className="flex flex-row justify-between">
                          <div>
                            <h4 className="font-bold mb-2">People</h4>
                            {latestMessage.direction ===
                            MessageDirection.OUTBOUND
                              ? renderTags(
                                  latestMessage.parent_recipients || [],
                                  latestMessage.student_recipients || [],
                                  latestMessage.tutor_recipients || [],
                                  false,
                                  conversationKey
                                )
                              : renderTags(
                                  latestMessage.matched_parents || [],
                                  latestMessage.matched_students || [],
                                  latestMessage.matched_tutors || [],
                                  false,
                                  conversationKey
                                )}
                          </div>
                        </div>
                        <div className="relative">
                          {/* Gradient hint at top */}
                          <div
                            className={`absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-gray-50 to-transparent pointer-events-none transition-opacity duration-200 ${
                              hasMoreAbove ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          <div
                            className="space-y-4 max-h-[300px] overflow-y-auto p-2 pb-6 border border-gray-200"
                            ref={messagesContainerRef}
                            onScroll={handleScroll}
                          >
                            {conversationMessages.map((message) => (
                              <div
                                key={message.id}
                                className={`flex ${
                                  message.direction === MessageDirection.INBOUND
                                    ? "justify-start"
                                    : "justify-end"
                                }`}
                              >
                                <div
                                  className={`max-w-[70%] rounded-lg p-3 ${
                                    message.direction ===
                                    MessageDirection.INBOUND
                                      ? "bg-green-100"
                                      : "bg-blue-100"
                                  }`}
                                >
                                  <div className="text-xs text-gray-500 mb-1">
                                    {moment(message.created_at).format(
                                      "MMM Do, h:mm a"
                                    )}
                                  </div>
                                  <div className="font-semibold mb-1">
                                    {message.subject}
                                  </div>
                                  <div className="whitespace-pre-wrap">
                                    {message.content}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 flex flex-col gap-2">
                            {!latestInboundMessage &&
                              conversationKey.includes("email") && (
                                <input
                                  type="text"
                                  value={subjectInput}
                                  onChange={(e) =>
                                    setSubjectInput(e.target.value)
                                  }
                                  placeholder="Enter subject..."
                                  className="w-1/6 p-2 border border-gray-300 rounded-xl"
                                />
                              )}
                            <div className="flex gap-2">
                              <textarea
                                value={replyContent}
                                onChange={(e) =>
                                  setReplyContent(e.target.value)
                                }
                                placeholder={`Type your ${
                                  conversationKey.includes("sms")
                                    ? "SMS"
                                    : "email"
                                }...`}
                                className="flex-1 p-2 border border-gray-300 rounded-xl resize-none h-[40px] min-h-[40px]"
                                style={{
                                  overflow: "hidden",
                                  lineHeight: "24px",
                                }}
                                onInput={(e) => {
                                  const target =
                                    e.target as HTMLTextAreaElement;
                                  target.style.height = "auto";
                                  target.style.height = `${target.scrollHeight}px`;
                                }}
                              />
                              <Button
                                size={ButtonSize.SMALL}
                                color={
                                  !replyContent.trim() ||
                                  sendingReply ||
                                  (!latestInboundMessage &&
                                    conversationKey.includes("email") &&
                                    !subjectInput.trim())
                                    ? ButtonColor.DARK_GRAY
                                    : ButtonColor.DARK_BLUE
                                }
                                fill={ButtonFill.HOLLOW}
                                onClick={() =>
                                  handleSendMessage(conversationMessages)
                                }
                                disabled={
                                  !replyContent.trim() ||
                                  sendingReply ||
                                  (!latestInboundMessage &&
                                    conversationKey.includes("email") &&
                                    !subjectInput.trim())
                                }
                              >
                                {sendingReply ? "Sending..." : "Send"}{" "}
                                <PaperPlaneIcon />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          }
        )}
      </tbody>
    </table>
  );
}
